import LongPoll, { PollStatuses } from './longPoll';
import { showToastViaEvent, closeToastViaEvent } from './ToastEvents';
import api from '../api/index';
import { BulkUploadStatus } from '../constants/bulkUpload';
import { BulkUploadDetailsSchemaContract } from '../schemas/BulkUploadDetailsSchema';
import { PollingAction } from 'types';

// A large bulk upload for a rearch org can legitimately take 30+ minutes to
// execute (e.g. Stackly with 500+ rows took ~17 minutes). We keep polling as
// long as `percentage_completed` is advancing, and only give up when progress
// has been flat for STALL_TIMEOUT_MS. MAX_POLL_DURATION_MS is a hard safety net
// so a wedged job doesn't keep the tab polling forever.
export const STALL_TIMEOUT_MS = 15 * 60 * 1000;
export const MAX_POLL_DURATION_MS = 1 * 60 * 60 * 1000;
// A single transient fetch error (e.g. an edge-gateway 429 that never reaches
// the backend) must not kill the polling loop — LongPoll treats any thrown
// error as terminal. Absorb up to this many consecutive failures before
// surfacing the timeout UX (the record may well still complete server-side).
export const MAX_CONSECUTIVE_FETCH_FAILURES = 5;

function pollForBulkUploads(id: number,
    action: PollingAction,
    tries: number,
    interval: number,
    setBulkUploadRecord: (bulkUploadRecord: BulkUploadDetailsSchemaContract[0] | null) => void,
    onSuccess?: () => void,
    onFailure?: () => void,
    onTimeout?: (data: BulkUploadDetailsSchemaContract[0] | null) => void,
    now: () => number = () => Date.now()) {
    sessionStorage.setItem('bulkUploadPollId', String(id));

    let lastPercentage: number | null | undefined = undefined;
    let lastChangedAt: number = now();
    let consecutiveFetchFailures = 0;

    const poll: LongPoll<BulkUploadDetailsSchemaContract[0]> = new LongPoll({
        pollFunction: async () => {
            if (sessionStorage.getItem('bulkUploadPollId')) {
                let apiResponse;
                try {
                    apiResponse = await api.bulkUploads.fetchBulkUploadRecord(id);
                } catch (e) {
                    console.error('bulk-upload poll fetch failed', e);
                    consecutiveFetchFailures += 1;
                    // LongPoll passes its lastData (the most recent successful
                    // record) to timeoutCallback, so no data is attached here.
                    if (consecutiveFetchFailures >= MAX_CONSECUTIVE_FETCH_FAILURES) {
                        return {
                            status: PollStatuses.POLL_STATUS_TIMEOUT,
                        };
                    }
                    return {
                        status: PollStatuses.POLL_STATUS_PROCESSING,
                    };
                }
                consecutiveFetchFailures = 0;

                if (poll.stopped) {
                    return {
                        status: PollStatuses.POLL_STATUS_CANCELLED,
                    };
                }

                setBulkUploadRecord(apiResponse[0]);

                switch (apiResponse[0].status) {
                    case BulkUploadStatus.STATUS_VALIDATION_FAILED:
                        throw 'File upload failed';

                    case BulkUploadStatus.STATUS_EXECUTION_FAILED:
                        throw 'Bulk upload execution failed';

                    case BulkUploadStatus.STATUS_VALIDATION_SUCCESS:
                    case BulkUploadStatus.STATUS_EXECUTION_SUCCESS:
                        return {
                            status: PollStatuses.POLL_STATUS_SUCCESS,
                            data: apiResponse[0]
                        };

                    case BulkUploadStatus.STATUS_CANCELED:
                        return {
                            status: PollStatuses.POLL_STATUS_CANCELLED,
                        };

                    default: {
                        const currentPercentage = apiResponse[0].percentage_completed;
                        if (currentPercentage !== lastPercentage) {
                            lastPercentage = currentPercentage;
                            lastChangedAt = now();
                        } else if (action === 'file-execution' && now() - lastChangedAt >= STALL_TIMEOUT_MS) {
                            return {
                                status: PollStatuses.POLL_STATUS_TIMEOUT,
                                data: apiResponse[0]
                            };
                        }
                        return {
                            status: PollStatuses.POLL_STATUS_PROCESSING,
                            data: apiResponse[0]
                        };
                    }
                }

            }
            return {
                status: PollStatuses.POLL_STATUS_CANCELLED,
            };
        },
        successCallback: (data: BulkUploadDetailsSchemaContract[0]) => {
            sessionStorage.removeItem('bulkUploadPollId');
            closeToastViaEvent('bulk-upload-' + id);
            if (action === 'file-upload') {
                showToastViaEvent({
                    text: 'Your file upload was successful',
                    timeout: 5000,
                    type: 'success',
                    icon: 'success',
                });
            }


            if (onSuccess && (data.status === BulkUploadStatus.STATUS_VALIDATION_SUCCESS || data.status === BulkUploadStatus.STATUS_EXECUTION_SUCCESS)) {
                onSuccess();
            }

            else if (onFailure && (data.status === BulkUploadStatus.STATUS_VALIDATION_FAILED || data.status === BulkUploadStatus.STATUS_EXECUTION_FAILED)) {
                onFailure();
            }

        },
        failureCallback: (e) => {
            sessionStorage.removeItem('bulkUploadPollId');
            const error = e || 'There was an error performing this bulk action';
            closeToastViaEvent('bulk-upload-' + id);
            showToastViaEvent({
                text: error,
                timeout: 5000,
                type: 'danger',
                icon: 'info',
            });
        },
        cancelCallback: () => {
            sessionStorage.removeItem('bulkUploadPollId');
            setBulkUploadRecord(null);
        },
        timeoutCallback: (data: BulkUploadDetailsSchemaContract[0] | null) => {
            sessionStorage.removeItem('bulkUploadPollId');
            closeToastViaEvent('bulk-upload-' + id);
            if (action === 'file-execution') {
                showToastViaEvent({
                    text:
                        'Your bulk upload is still processing in the background. ' +
                        'Refresh the page later to check its final status.',
                    timeout: 8000,
                    type: 'success',
                    icon: 'info',
                });
                if (onTimeout) {
                    onTimeout(data);
                }
            } else {
                showToastViaEvent({
                    text: 'Polling timed out',
                    timeout: 5000,
                    type: 'danger',
                    icon: 'info',
                });
            }
        },
        tries: tries,
        pollInterval: interval,
    });


    poll.start();

    return poll;
}

export default pollForBulkUploads;
